import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Loading from 'Components/Atoms/Loading/Loading';
import Button from 'Components/Atoms/Button/Button';
import Icon from 'Components/Atoms/Icon/Icon';
import Spinner from 'Components/Atoms/Spinner';
import TextButton from 'Components/Molecules/TextButton';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import useDrawerStack from 'Hooks/useDrawerStack';
import { associateRecurrence } from 'Services/invoices';
import { trackError } from 'Utils/errorMonitoring';
import { DRAWER_STACK, INVOICE_TYPES, INVOICES_RECONCILED_STATUS, OPERATIONS_TYPES } from 'Utils/constants';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import { cn } from 'Utils/cn';
import { formatCurrency } from 'Utils/formatters';
import { REPLAY_BLOCK_CLASS } from 'Utils/constants';
import useCurrency from 'Hooks/useCurrency';
import useLocale from 'Hooks/useLocale';
import RecurrenceAmount from 'Components/Molecules/RecurrenceAmount/RecurrenceAmount';
import useInfiniteForecastInspectPending from './useInfiniteForecastInspectPending';
import ForecastValueInvoiceRow from './ForecastValueInvoiceRow';
import ForecastValueStatusBadge from './ForecastValueStatusBadge';

const shouldBeEnabled = ({ invoice, operation }) => {
    if (!invoice || !operation) return true;

    if (invoice.type === INVOICE_TYPES.SALE && operation.invoice_type === OPERATIONS_TYPES.CASH_IN) return true;
    if (invoice.type === INVOICE_TYPES.PURCHASE && operation.invoice_type === OPERATIONS_TYPES.CASH_OUT) return true;

    return false;
};

export default function ForecastValueInvoices(props) {
    const { cell, forecastId, periodicity } = props;

    const [operationSelected, setOperationSelected] = useState(null);
    const [invoiceSelected, setInvoiceSelected] = useState(null);
    const [processing, setProcessing] = useState(false);

    const { open } = useDrawerStack();
    const { t } = useTranslation('calendar');
    const { t: tForecasts } = useTranslation('forecasts');
    const currency = useCurrency();
    const locale = useLocale();
    const queryClient = useQueryClient();

    const { invoices, recurrences, count, isLoading, hasNextPage, isFetching, fetchNextPage, refetch } =
        useInfiniteForecastInspectPending(forecastId, cell.filters, { includeReconciled: true });

    const handleAssociate = async () => {
        if (!operationSelected || !invoiceSelected) return;

        setProcessing(true);
        try {
            await associateRecurrence({
                recurrenceId: operationSelected.recurrence.id,
                invoiceId: invoiceSelected.id,
                periodKey: cell.filters.date_from,
                periodicity,
            });

            await Promise.all([queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId] }), refetch()]);

            setOperationSelected(null);
            setInvoiceSelected(null);
        } catch (error) {
            trackError(error);
        } finally {
            setProcessing(false);
        }
    };

    const openInvoice = (invoice) => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId: invoice.id,
            title: <InvoiceTitle invoice={invoice} />,
            drawerStackProps: {
                size: 'lg',
            },
        });
    };

    const hasItems = invoices.length > 0 || recurrences.length > 0;
    const itemsCount = (count ?? invoices.length) + recurrences.length;
    const showAssociateButton = !!operationSelected || !!invoiceSelected;
    const associateCount = operationSelected && invoiceSelected ? 2 : 1;

    return (
        <div className="flex flex-col h-full">
            {isLoading && (
                <div className="py-6 flex justify-center">
                    <Loading />
                </div>
            )}

            <div className="flex h-8 mb-2 items-center justify-start gap-2 px-4">
                {showAssociateButton ? (
                    <>
                        <TextButton
                            color="black"
                            disabled={associateCount !== 2 || processing}
                            className="inline-flex gap-2"
                            onClick={handleAssociate}
                        >
                            <Icon name="join" size={22} color="currentColor" />
                            <span className="small">
                                {t('associate')} {associateCount} / 2
                            </span>
                        </TextButton>

                        {processing && <Spinner size={24} />}
                    </>
                ) : (
                    !isLoading && (
                        <div className={cn('text-sm font-medium text-dark-blue/50', REPLAY_BLOCK_CLASS)}>
                            {formatCurrency(cell.amount_forecasted ?? 0, { currency, locale })}{' '}
                            {tForecasts('invoices-recurrences-list-title', { count: itemsCount })}
                        </div>
                    )
                )}
            </div>

            {hasItems && (
                <div className="pb-6">
                    <div className="bg-white rounded-md shadow-card">
                        {recurrences.map((operation) => {
                            const id = operation.recurrence.id;
                            const checked = operationSelected?.recurrence?.id === id;
                            const isEnabled = shouldBeEnabled({ invoice: invoiceSelected, operation });
                            const isDisabled = (operationSelected !== null || !isEnabled) && !checked;

                            return (
                                <div key={id} className="px-5 py-3 not-last:border-b border-gray-200">
                                    <div
                                        className={cn(
                                            'flex items-center gap-3',
                                            isDisabled && 'opacity-50 pointer-events-none',
                                        )}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onChange={(ev) => {
                                                const checked = ev.target.checked;
                                                setOperationSelected(checked ? operation : null);
                                            }}
                                        />

                                        <div className="flex gap-3 grow items-center min-w-0 text-sm">
                                            <div className="flex items-center gap-2 grow min-w-0">
                                                <span className="truncate">{operation.description}</span>
                                                <ForecastValueStatusBadge />
                                            </div>

                                            <RecurrenceAmount operation={operation} />
                                            <div className="shrink-0 w-8 flex pl-1 items-center">
                                                <Tooltip label={t('recurrence')}>
                                                    <span>
                                                        <Icon name="rotate-ccw" color="#919191" size={16} />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {invoices.map((invoice) => {
                            const isCompleted = invoice.reconciled_status === INVOICES_RECONCILED_STATUS.RECONCILED;

                            if (isCompleted) {
                                return (
                                    <ForecastValueInvoiceRow key={invoice.id} invoice={invoice} onOpen={openInvoice} />
                                );
                            }

                            const checked = invoiceSelected?.id === invoice.id;
                            const isEnabled = shouldBeEnabled({ invoice, operation: operationSelected });
                            const isDisabled = (invoiceSelected !== null || !isEnabled) && !checked;

                            return (
                                <ForecastValueInvoiceRow
                                    key={invoice.id}
                                    invoice={invoice}
                                    selectable
                                    checked={checked}
                                    disabled={isDisabled}
                                    onCheckedChange={(checked) => setInvoiceSelected(checked ? invoice : null)}
                                    onOpen={openInvoice}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {!isLoading && !hasItems && (
                <div className="text-center py-5 my-12">
                    <span className="opacity-50">{tForecasts('invoices-no-results')}</span>
                </div>
            )}

            {hasNextPage && (
                <div className={'mt-auto pb-4 text-center'}>
                    {isFetching ? (
                        <Loading size={48} />
                    ) : (
                        <Button onClick={fetchNextPage} size="small">
                            {tForecasts('load-more')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

ForecastValueInvoices.propTypes = {
    cell: PropTypes.object.isRequired,
    forecastId: PropTypes.string.isRequired,
    periodicity: PropTypes.string,
};
